import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import type { IClientCacheStorage } from '../../services/clients';
import {
  ACCOUNT_TAB_CACHE_KEYS,
  type AccountSharedSnapshotCache,
  type AccountTabCacheState,
  AccountPracInfoService,
} from '../../services/account';
import {
  AccountPracInfoStorage,
} from '../../storage/AccountPracInfoStorage';
import type {
  AccountPracInfoRequestDto,
  AccountPracInfoResponseDto,
  AccountPracInfoType,
} from '../../types/api';
import { AccountSettingsView } from './account/AccountSettingsView';
import { SubscriptionView } from './account/SubscriptionView';
import { TouchpointsView } from './account/TouchpointsView';
import { AssessmentOptionsView } from './account/AssessmentOptionsView';
import { ManageTeamView } from './account/ManageTeamView';
import { ManageAccountsView } from './account/ManageAccountsView';
import { useActiveAccountStore } from '../../stores/activeAccountStore';
import { resolveAuthContext } from '../../services/auth/authContext';

interface ProGroupsAccountViewProps {
  storage?: any;
  apiService?: any;
  clientCacheStorage?: IClientCacheStorage;
  initialSubsection?: string;
}

type AccountSubsection =
  | 'Subscription'
  | 'Account Settings'
  | 'Assessment Options'
  | 'Touchpoints'
  | 'Manage Team'
  | 'Manage Accounts';

const ACCOUNT_SETTINGS_TAB_KEY = ACCOUNT_TAB_CACHE_KEYS.accountSettings;

// Touchpoints now also travels with AccountSettings here (it's normally only in the
// background-only list below) because AccountSettingsView's "Enable schedule management"
// edit panel needs both from the SAME response: the merge in hydrateStorageAndRefresh below
// (`{ ...latestStored, ...dto }`) replaces AccountSettings wholesale, so if this fetch's own
// dto.AccountSettings.Registries ever came back incomplete on its own (e.g. it depends on the
// backend also being asked for Touchpoints in the same request), it would silently clobber a
// previously-good Registries list written earlier by the background prefetch. Still requested
// in ACCOUNT_BACKGROUND_TYPES too — hasAccountSettingsData() below only checks for
// AccountSettings, so a cache hit can skip this fetch entirely, and the background prefetch is
// what still guarantees Touchpoints gets fetched at all in that case.
const ACCOUNT_SETTINGS_TYPES: AccountPracInfoType[] = [
  'AccountSettings',
  'Touchpoints',
];

const ACCOUNT_BACKGROUND_TYPES: AccountPracInfoType[] = [
  'Subscriptions',
  'Touchpoints',
  'AdminOptions',
  'AccountSras',
];

const resolveAccountSubsection = (initialSubsection?: string): AccountSubsection => {
  switch (initialSubsection) {
    case 'Subscription':
    case 'Account Settings':
    case 'Assessment Options':
    case 'Touchpoints':
    case 'Manage Team':
    case 'Manage Accounts':
      return initialSubsection;
    default:
      return 'Account Settings';
  }
};

const resolveCacheStorage = (storage?: IClientCacheStorage): IClientCacheStorage | null => {
  return storage
    || ((globalThis as any).__proGroupsClientCacheStorage as IClientCacheStorage | undefined)
    || ((globalThis as any).__childNavClientCacheStorage as IClientCacheStorage | undefined)
    || null;
};

const resolveApiService = (apiService?: any): any => {
  return apiService
    || (globalThis as any).__proGroupsApiService
    || (globalThis as any).__childNavApiService;
};

const resolveUserStore = (): any => resolveAuthContext().userStore;

const resolveAccountId = (): string | null => {
  const userStore = resolveUserStore();
  const state = userStore?.getState ? userStore.getState() : null;
  return state?.session?.user?.familyId
    || state?.session?.familyAccount?.familyId
    || null;
};

const createEmptySnapshot = (): AccountSharedSnapshotCache => ({
  accountId: '',
  types: ACCOUNT_SETTINGS_TYPES,
  data: null,
  refreshedAt: null,
  error: null,
});

const getAccountPrefetchRegistry = (): Record<string, boolean> => {
  const globalScope = globalThis as any;
  if (!globalScope.__proGroupsAccountPracInfoPrefetchedByAccountId) {
    globalScope.__proGroupsAccountPracInfoPrefetchedByAccountId = {};
  }
  return globalScope.__proGroupsAccountPracInfoPrefetchedByAccountId as Record<string, boolean>;
};

const hasAccountPrefetchedInSession = (accountId: string): boolean => {
  const result = !!getAccountPrefetchRegistry()[accountId];
  return result;
};

const markAccountPrefetchedInSession = (accountId: string): void => {
  getAccountPrefetchRegistry()[accountId] = true;
};

const mergeTypes = (current: AccountPracInfoType[], incoming: AccountPracInfoType[]): AccountPracInfoType[] => {
  return Array.from(new Set<AccountPracInfoType>([...current, ...incoming]));
};

const getAccountHydratedRegistry = (): Record<string, boolean> => {
  const globalScope = globalThis as any;
  if (!globalScope.__proGroupsAccountPracInfoHydratedByAccountId) {
    globalScope.__proGroupsAccountPracInfoHydratedByAccountId = {};
  }
  return globalScope.__proGroupsAccountPracInfoHydratedByAccountId as Record<string, boolean>;
};

const getAccountInFlightRegistry = (): Record<string, boolean> => {
  const globalScope = globalThis as any;
  if (!globalScope.__proGroupsAccountPracInfoInFlightByAccountId) {
    globalScope.__proGroupsAccountPracInfoInFlightByAccountId = {};
  }
  return globalScope.__proGroupsAccountPracInfoInFlightByAccountId as Record<string, boolean>;
};

const isAccountHydratedInSession = (accountId: string): boolean => {
  const result = !!getAccountHydratedRegistry()[accountId];
  return result;
};

const markAccountHydratedInSession = (accountId: string): void => {
  getAccountHydratedRegistry()[accountId] = true;
};

const isAccountFetchInFlight = (accountId: string): boolean => {
  return !!getAccountInFlightRegistry()[accountId];
};

const setAccountFetchInFlight = (accountId: string, value: boolean): void => {
  getAccountInFlightRegistry()[accountId] = value;
};

const hasAccountSettingsData = (data: AccountPracInfoResponseDto | null | undefined): boolean => {
  return !!data?.AccountSettings;
};

const getAccountSettingsStrength = (
  data: AccountPracInfoResponseDto | null | undefined
): number => {
  const settings = data?.AccountSettings;
  if (!settings) {
    return 0;
  }

  const enablesCount = settings.Enables?.length ?? 0;
  const plaCount = settings.PlaList?.length ?? 0;
  const pracNameScore = settings.PracInfo?.DName ? 1 : 0;
  const adminEmailScore = data?.SraUser?.Email ? 1 : 0;
  // Registries/PracPhi drive AccountSettingsView's own dual-list selection state (schedule
  // management / client info management) — without counting them, a background response whose
  // AccountSettings carries full Enables/PlaList/PracInfo/SraEmail but an incomplete Registries
  // (e.g. this request didn't ask for Touchpoints) scores no weaker than a previously-fetched
  // response that had the real Registries list, so it wins the merge below and silently wipes
  // out the correct selection.
  const registriesCount = settings.Registries?.length ?? 0;
  const pracPhiCount = settings.PracPhi?.length ?? 0;

  return enablesCount + plaCount + pracNameScore + adminEmailScore + registriesCount + pracPhiCount;
};

const resolveBackgroundMergedAccountData = (
  baseData: AccountPracInfoResponseDto | null | undefined,
  incomingData: AccountPracInfoResponseDto,
  options: { baselineSavedAt?: string | null } = {}
): AccountPracInfoResponseDto => {
  const { baselineSavedAt = null } = options;

  const merged = {
    ...(baseData ?? {} as AccountPracInfoResponseDto),
    ...incomingData,
  } as AccountPracInfoResponseDto;

  const baseSettingsStrength = getAccountSettingsStrength(baseData);
  const incomingSettingsStrength = getAccountSettingsStrength(incomingData);
  const shouldPreserveBaseSettings = !!baseData?.AccountSettings && (
    !incomingData?.AccountSettings
    || incomingSettingsStrength < baseSettingsStrength
  );

  if (shouldPreserveBaseSettings) {
    merged.AccountSettings = baseData!.AccountSettings;
  }

  const incomingSraEmail = incomingData?.SraUser?.Email?.trim() ?? '';
  const baseSraEmail = baseData?.SraUser?.Email?.trim() ?? '';
  if (!incomingSraEmail && baseSraEmail) {
    merged.SraUser = baseData!.SraUser;
  }

  // BillPayInfo has no count-based "strength" the way AccountSettings does (an Add increases
  // Subscriptions.length, a Cancel decreases it, so size can't indicate recency) — instead,
  // detect staleness via storage's own _savedAt: if it changed between when this background
  // fetch was issued (baselineSavedAt) and now, something else (e.g. a SubscriptionView
  // billpay action) wrote to storage while this fetch was in flight, and incomingData reflects
  // the world before that write — baseData.BillPayInfo must win.
  const baseSavedAt = (baseData as any)?._savedAt ?? null;
  const baseDataChangedSinceFetchIssued =
    baselineSavedAt != null && baseSavedAt != null && baseSavedAt !== baselineSavedAt;

  if (baseDataChangedSinceFetchIssued && baseData?.BillPayInfo) {
    merged.BillPayInfo = baseData.BillPayInfo;
  }

  return merged;
};

const getAccountSettingsPayloadSignature = (
  accountId: string,
  data: AccountPracInfoResponseDto | null | undefined
): string => {
  if (!data) {
    return `${accountId}|none`;
  }

  const settings = data.AccountSettings;
  const savedAt = (data as any)._savedAt ?? '';
  const enablesCount = settings?.Enables?.length ?? 0;
  const plaCount = settings?.PlaList?.length ?? 0;
  const sraUserEmail = data.SraUser?.Email ?? '';
  const pracName = settings?.PracInfo?.DName ?? '';

  return `${accountId}|${savedAt}|${enablesCount}|${plaCount}|${sraUserEmail}|${pracName}`;
};

const AccountSubsectionPlaceholder: React.FC<{ subsectionTitle: string }> = ({ subsectionTitle }) => (
  <View style={styles.placeholderPanel}>
    <View style={styles.placeholderCard}>
      <Text style={styles.placeholderTitle}>{subsectionTitle}</Text>
      <Text style={styles.placeholderText}>This account subsection is queued for migration.</Text>
    </View>
  </View>
);

export const ProGroupsAccountView: React.FC<ProGroupsAccountViewProps> = ({
  storage,
  apiService,
  clientCacheStorage,
  initialSubsection,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [snapshot, setSnapshot] = useState<AccountSharedSnapshotCache>(createEmptySnapshot);
  const storageHydrationSignatureRef = useRef<string>('');

  const resolvedCacheStorage = resolveCacheStorage(clientCacheStorage);
  const resolvedApiService = resolveApiService(apiService);
  const activeSubsection = resolveAccountSubsection(initialSubsection);
  const maxWidth = activeSubsection === 'Manage Team' || activeSubsection === 'Manage Accounts' ? 980 : 700;
  
  // Get accountId from activeAccountStore (source of truth for which account to display)
  const activeAccountId = useActiveAccountStore(state => state.activeAccountId);
  const accountId = activeAccountId || resolveAccountId();

  const pracInfoService = useMemo(
    () => (resolvedApiService ? new AccountPracInfoService(resolvedApiService) : null),
    [resolvedApiService]
  );

  useEffect(() => {
    let cancelled = false;

    const hydrateCache = async () => {
      // PRIORITY 1: Check AccountPracInfoStorage (fresh backend data)
      const currentAccountId = activeAccountId || resolveAccountId();
      if (!currentAccountId) {
        console.log('⚠️ ProGroupsAccountView: No accountId available, skipping hydration');
        setIsHydrated(true);
        return;
      }

      // Always check backend storage first (source of truth)
      if (storage) {
        const pracStorage = new AccountPracInfoStorage(storage);
        const stored = await pracStorage.getAccountPracInfo(currentAccountId);
        if (!cancelled && stored) {
          const storedDto = stored as AccountPracInfoResponseDto;

          setSnapshot({
            accountId: currentAccountId,
            types: ACCOUNT_SETTINGS_TYPES,
            data: storedDto,
            refreshedAt: (stored as any)._savedAt ?? new Date().toISOString(),
            error: null,
          });
          
          if (hasAccountSettingsData(storedDto)) {
            markAccountHydratedInSession(currentAccountId);
          }
          
          setIsHydrated(true);
          return;
        } 
      }

      // PRIORITY 2: Fall back to UI cache only if no backend data
      if (resolvedCacheStorage) {
        const cached = await resolvedCacheStorage.loadTabState<AccountTabCacheState>(ACCOUNT_SETTINGS_TAB_KEY);
        if (!cancelled && cached?.snapshot) {
          // Only use cache if it matches the current accountId
          if (cached.snapshot.accountId === currentAccountId) {
            setSnapshot(cached.snapshot);
            if (hasAccountSettingsData(cached.snapshot.data as AccountPracInfoResponseDto)) {
              markAccountHydratedInSession(currentAccountId);
            }
          } else {
            console.log('⚠️ ProGroupsAccountView: UI cache has wrong accountId (', cached.snapshot.accountId, ' vs ', currentAccountId, '), ignoring stale cache');
          }
        }
      }

      if (!cancelled) {
        setIsHydrated(true);
      }
    };

    void hydrateCache();

    return () => {
      cancelled = true;
    };
  }, [resolvedCacheStorage, storage, activeAccountId]);

  useEffect(() => {
    if (!isHydrated || !resolvedCacheStorage) {
      return;
    }

    void resolvedCacheStorage.saveTabState<AccountTabCacheState>(ACCOUNT_SETTINGS_TAB_KEY, {
      snapshot,
    });
  }, [isHydrated, resolvedCacheStorage, snapshot]);

  useEffect(() => {
    let cancelled = false;

    const hydrateStorageAndRefresh = async () => {
      if (!isHydrated) {
        return;
      }

      // Use activeAccountStore as source of truth
      const currentAccountId = activeAccountId || resolveAccountId();
      if (!currentAccountId) {
        return;
      }

      // Check if we already have data for the current account
      let hasHydratedData = !!(
        snapshot.accountId === currentAccountId
        && hasAccountSettingsData(snapshot.data)
      );

      // If we already have data or it's marked as hydrated, we're done
      if (hasHydratedData || isAccountHydratedInSession(currentAccountId)) {
        return;
      }

      // No data available - need to fetch from backend
      if (!pracInfoService) {
        console.log('⚠️ ProGroupsAccountView: No API service available for fetch');
        return;
      }

      if (isAccountFetchInFlight(currentAccountId)) {
        return;
      }

      setAccountFetchInFlight(currentAccountId, true);

      setIsLoading(true);
      try {
        const request: AccountPracInfoRequestDto = {
          AccountId: currentAccountId,
          Types: ACCOUNT_SETTINGS_TYPES,
        };

        const dto: AccountPracInfoResponseDto = await pracInfoService.getAccountPracInfo(request, {
          userStore: resolveUserStore(),
          storage,
        });

        if (cancelled) {
          return;
        }

        const refreshedAt = new Date().toISOString();

        // dto only ever carries Types: ['AccountSettings'] (this effect's sole job) — merge
        // onto whatever's currently in storage rather than replacing it outright, so this
        // doesn't silently drop BillPayInfo/Touchpoints/AdminOptions/AccountSras if they're
        // already present (e.g. from the background prefetch effect having already run).
        let mergedForStorage: AccountPracInfoResponseDto = dto;
        if (storage) {
          const pracStorage = new AccountPracInfoStorage(storage);
          const latestStored = await pracStorage.getAccountPracInfo(currentAccountId);
          mergedForStorage = latestStored ? { ...latestStored, ...dto } : dto;
          await pracStorage.saveAccountPracInfo(currentAccountId, mergedForStorage);
        }

        setSnapshot({
          accountId: currentAccountId,
          types: ACCOUNT_SETTINGS_TYPES,
          data: mergedForStorage,
          refreshedAt,
          error: null,
        });
        markAccountHydratedInSession(currentAccountId);

        // Dispatch event to refresh display settings based on new AccountSettings
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('progroups:sra-config-updated', {
            detail: { accountId: currentAccountId },
          }));
        }
      } catch (error) {
        if (cancelled) {
          return;
        }

        const message = error instanceof Error ? error.message : String(error);
        setSnapshot((current) => ({
          ...current,
          accountId: currentAccountId,
          error: message,
        }));
      } finally {
        setAccountFetchInFlight(currentAccountId, false);
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void hydrateStorageAndRefresh();

    return () => {
      cancelled = true;
    };
  }, [isHydrated, pracInfoService, storage, activeAccountId, snapshot.accountId, snapshot.data]);

  useEffect(() => {
    let cancelled = false;

    const prefetchAccountBackground = async () => {
      if (
        !isHydrated
        || (activeSubsection !== 'Account Settings'
          && activeSubsection !== 'Assessment Options'
          && activeSubsection !== 'Subscription'
          && activeSubsection !== 'Touchpoints')
      ) {
        return;
      }

      const currentAccountId = activeAccountId || resolveAccountId();

      if (!currentAccountId || !pracInfoService || hasAccountPrefetchedInSession(currentAccountId)) {
        return;
      }

      if (isAccountFetchInFlight(currentAccountId)) {
        return;
      }

      setAccountFetchInFlight(currentAccountId, true);
      try {
        // Captured immediately before issuing the fetch (not from `snapshot`, which can lag
        // storage) — resolveBackgroundMergedAccountData compares this against storage's
        // _savedAt at merge time to detect a concurrent local write (e.g. a SubscriptionView
        // billpay action) that landed while this fetch was in flight, so this fetch's now-stale
        // BillPayInfo doesn't overwrite it.
        let baselineSavedAt: string | null = null;
        if (storage) {
          const pracStorage = new AccountPracInfoStorage(storage);
          const storedBeforeFetch = await pracStorage.getAccountPracInfo(currentAccountId);
          baselineSavedAt = (storedBeforeFetch as any)?._savedAt ?? null;
        }

        const request: AccountPracInfoRequestDto = {
          AccountId: currentAccountId,
          Types: ACCOUNT_BACKGROUND_TYPES,
        };

        const dto = await pracInfoService.getAccountPracInfo(request, {
          userStore: resolveUserStore(),
          storage,
        });

        if (cancelled) {
          return;
        }

        // Mark prefetch complete immediately after a successful backend response
        // so downstream cache/merge failures do not retrigger network loops.
        markAccountPrefetchedInSession(currentAccountId);

        let baseData: AccountPracInfoResponseDto | null = snapshot.data;
        if (storage) {
          const pracStorage = new AccountPracInfoStorage(storage);
          const stored = await pracStorage.getAccountPracInfo(currentAccountId);
          if (stored) {
            baseData = stored as AccountPracInfoResponseDto;
          }
        }

        // Computed once and reused for both the state update and the storage write below —
        // previously these were two independent merge calls (one against this re-read
        // `baseData`, one against `current.data` inside the setSnapshot updater) that could
        // diverge from each other. Storage is never staler than React state here (every writer
        // in this file/SubscriptionView writes storage first, then notifies), so basing both
        // on this single re-read is strictly correct, not just simpler.
        const mergedData = resolveBackgroundMergedAccountData(baseData, dto, { baselineSavedAt });
        const refreshedAt = new Date().toISOString();

        setSnapshot((current) => ({
          ...current,
          accountId: currentAccountId,
          types: mergeTypes(current.types, ACCOUNT_BACKGROUND_TYPES),
          data: mergedData,
          refreshedAt,
        }));

        if (storage) {
          const pracStorage = new AccountPracInfoStorage(storage);
          try {
            await pracStorage.saveAccountPracInfo(currentAccountId, mergedData);
          } catch (saveError) {
            console.warn('ProGroupsAccountView: failed to persist background prefetch merge', saveError);
          }
        }

        // Deliberately NOT progroups:sra-config-updated: ProGroupsShell also listens for that
        // event to bump sraVisibilityRefreshToken, which is baked into this component's own
        // `key` prop and force-remounts the whole Account tree. Subscriptions/Touchpoints/
        // AdminOptions/AccountSras (this effect's fetched fields) have no bearing on SRA menu
        // visibility (governed by AccountSettings.Enables/SraUser.ConfigInfo, untouched here),
        // so there's no reason for this refresh to force that remount — doing so previously
        // compounded the BillPayInfo race into a persistent revert rather than a transient one.
        if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
          window.dispatchEvent(new CustomEvent('progroups:account-background-updated', {
            detail: { accountId: currentAccountId },
          }));
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('ProGroupsAccountView: background account prefetch failed', error);
        }
      } finally {
        setAccountFetchInFlight(currentAccountId, false);
      }
    };

    void prefetchAccountBackground();

    return () => {
      cancelled = true;
    };
  }, [activeSubsection, isHydrated, pracInfoService, storage, activeAccountId, snapshot.data]);

  // Listen for sra-config-updated / billpay-info-updated / account-background-updated events
  // and refresh account data from cache. These are deliberately separate event names sharing
  // one handler: progroups:sra-config-updated is ALSO watched by ProGroupsShell, which bumps
  // sraVisibilityRefreshToken on every occurrence — baked into this component's own `key`
  // prop, so that event force-remounts this whole tree. That's fine for actual SRA/menu
  // visibility changes, but SubscriptionView's billing actions (add/change/cancel) and this
  // file's own background prefetch only need this component's cached-data re-read below, not
  // a remount — a remount mid-flow (e.g. Change's cancel-then-add) tore down the in-flight
  // action before its result ever landed, and also compounded the BillPayInfo staleness race
  // (see resolveBackgroundMergedAccountData / prefetchAccountBackground). billpay-info-updated
  // (SubscriptionView) and account-background-updated (this file's prefetch effect) both get
  // the same refresh without that side effect.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
      return;
    }

    const handleConfigUpdated = async (event: Event) => {
      const customEvent = event as CustomEvent;
      const updatedAccountId = customEvent.detail?.accountId;

      console.log('ProGroupsAccountView: received', customEvent.type, 'for accountId:', updatedAccountId);
      console.log('ProGroupsAccountView: current accountId:', accountId);

      // Compare against accountId (the resolveAccountId()-fallback-aware value passed down to
      // children like SubscriptionView, which is what they stamp into the event) rather than
      // the raw activeAccountId store value — activeAccountId is empty for a non-elevated
      // account viewing its own data (no multi-account selection in play), which silently
      // dropped every refresh for that case even though the event's accountId was correct.
      if (updatedAccountId && updatedAccountId === accountId && storage) {
        try {
          const pracStorage = new AccountPracInfoStorage(storage);
          const refreshedData = await pracStorage.getAccountPracInfo(updatedAccountId);

          if (refreshedData) {
            console.log('ProGroupsAccountView: refreshed account data from cache with updated Pos values');
            setSnapshot((current) => ({
              ...current,
              data: refreshedData,
              refreshedAt: new Date().toISOString(),
            }));
          }
        } catch (error) {
          console.warn('ProGroupsAccountView: failed to refresh account data after config update', error);
        }
      }
    };

    window.addEventListener('progroups:sra-config-updated', handleConfigUpdated as EventListener);
    window.addEventListener('progroups:billpay-info-updated', handleConfigUpdated as EventListener);
    window.addEventListener('progroups:account-background-updated', handleConfigUpdated as EventListener);

    return () => {
      window.removeEventListener('progroups:sra-config-updated', handleConfigUpdated as EventListener);
      window.removeEventListener('progroups:billpay-info-updated', handleConfigUpdated as EventListener);
      window.removeEventListener('progroups:account-background-updated', handleConfigUpdated as EventListener);
    };
  }, [accountId, storage]);

  return (
    <View style={styles.container}>
      <View style={[styles.mainCardFrame, { maxWidth }]}>
        {activeSubsection === 'Subscription' ? (
          <SubscriptionView
            accountData={snapshot.data}
            isLoading={isLoading}
            error={snapshot.error}
            storage={storage}
            accountId={accountId}
            apiService={resolvedApiService}
          />
        ) : activeSubsection === 'Account Settings' ? (
          <AccountSettingsView
            accountData={snapshot.data}
            isLoading={isLoading}
            error={snapshot.error}
            storage={storage}
            accountId={accountId}
            apiService={resolvedApiService}
          />
        ) : activeSubsection === 'Assessment Options' ? (
          <AssessmentOptionsView
            accountData={snapshot.data}
            isLoading={isLoading}
            error={snapshot.error}
            storage={storage}
            accountId={accountId}
            apiService={resolvedApiService}
          />
        ) : activeSubsection === 'Touchpoints' ? (
          <TouchpointsView
            accountData={snapshot.data}
            isLoading={isLoading}
            error={snapshot.error}
            storage={storage}
            accountId={accountId}
            apiService={resolvedApiService}
          />
        ) : activeSubsection === 'Manage Team' ? (
          <ManageTeamView
            accountData={snapshot.data}
            isLoading={isLoading}
            error={snapshot.error}
            accountId={accountId}
            storage={storage}
            apiService={resolvedApiService}
          />
        ) : activeSubsection === 'Manage Accounts' ? (
          <ManageAccountsView
            apiService={resolvedApiService}
            storage={storage}
          />
        ) : (
          <AccountSubsectionPlaceholder subsectionTitle={activeSubsection} />
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 24,
    backgroundColor: '#ffffff',
  },
  mainCardFrame: {
    alignSelf: 'center',
    width: '100%',
    backgroundColor: '#ffffff',
    borderRadius: 16,
    borderWidth: 0,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    elevation: 0,
  },
  placeholderPanel: {
    padding: 20,
  },
  placeholderCard: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 12,
    padding: 20,
  },
  placeholderTitle: {
    fontSize: 18,
    fontWeight: '800',
    color: '#0f172a',
    marginBottom: 8,
  },
  placeholderText: {
    fontSize: 14,
    color: '#475569',
    lineHeight: 22,
  },
});
